import React, { useEffect, useState } from 'react';

import {
    ChevronDownIcon,
    ChevronUpIcon,
} from 'components/opsVisibility/components/inventoryHistoryV2/filterComponentTailwind/Icons';

import { AdjustmentReasonModal } from './AdjustmentReasonModal';
import { ExportEventsButton } from './ExportEventsButton';
import { NumberDisplay } from './NumberDisplay';
import { ValueTooltip } from './ValueTooltip';
import { EventsExpandedContainer } from './customExpansionComponents/EventsExpandedContainer';

import '../inventoryHistoryV2.css';

/**
 * A generic collapsible row component for tables
 *
 * @param {object} row - The data for this row
 * @param {array} columns - Array of column definitions
 * @param {array} expandedFilters - Array of expanded filters
 * @param {function} setNotification - Function to set notifications
 * @param {function} filtersToApiParams - Function to convert filters to API parameters
 * @param {string} facilityId - The facility ID for the current view
 * @param {boolean} summarizePackout - Whether to summarize packout events
 * @param {function} fetchAdjustmentReasons - Function to fetch adjustment reasons for modal
 * @param {string} groupBy - The current grouping mode ('sku' or 'lot')
 */
function CollapsibleRow({
    columns,
    expandedFilters = [],
    facilityId,
    fetchAdjustmentReasons,
    filtersToApiParams,
    groupBy,
    groupByType,
    row,
    setNotification,
    summarizePackout = false,
}) {
    const [open, setOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [adjustmentType, setAdjustmentType] = useState(null); // 'positive' or 'negative'
    const [effectiveGroupBy, setEffectiveGroupBy] = useState('sku'); // Default to 'sku'

    // Determine the effective grouping mode based on props and columns
    useEffect(() => {
        // If groupBy is explicitly provided, use it
        if (groupBy) {
            setEffectiveGroupBy(groupBy);
            return;
        }

        // Otherwise, infer from the identity column
        const identityColumnId = columns[0]?.id;
        if (identityColumnId === 'lotId') {
            setEffectiveGroupBy('lot');
        } else {
            setEffectiveGroupBy('sku');
        }
    }, [groupBy, columns]);

    const handleToggleExpand = () => {
        setOpen(!open);
    };

    // Handler to open modal for adjustment details
    const handleOpenAdjustmentModal = (type, event) => {
        // Prevent the tooltip from showing
        event.stopPropagation();

        setAdjustmentType(type);
        setIsModalOpen(true);
    };

    // Determine if a column should use change value formatting with +/- signs
    const isChangeValueColumn = (columnId) => {
        const changeColumns = [
            'receivedUnits',
            'packedOutUnits',
            'adjustedDownUnits',
            'adjustedUpUnits',
            'systemAdjustmentUnits',
            'negativeAdjustments',
            'difference',
        ];
        return changeColumns.includes(columnId);
    };

    // Create tooltip content for the Inventory Variance (difference) column
    const createDifferenceTooltip = (rowData) => {
        const expectedEnding = rowData.endingUnitsWithoutAdjustments || 0;
        const actualEnding = rowData.endingUnits || 0;
        const difference = rowData.difference || 0;

        return (
            <div className="py-1 px-2">
                {/* Labels row with 5 columns, empty spans for operators */}
                <div className="grid grid-cols-5 gap-x-2 text-center mb-1">
                    <span className="text-xs font-medium">Expected Ending Inventory</span>
                    <span />
                    <span className="text-xs font-medium">Actual Ending Inventory</span>
                    <span />
                    <span className="text-xs font-medium">Inventory Variance</span>
                </div>
                {/* Values row with operators */}
                <div className="grid grid-cols-5 gap-x-2 text-center items-center">
                    <NumberDisplay value={expectedEnding} isChangeValue={false} className="font-bold text-xs" />
                    <span className="font-bold text-xs">−</span>
                    <NumberDisplay value={actualEnding} isChangeValue={false} className="font-bold text-xs" />
                    <span className="font-bold text-xs">=</span>
                    <NumberDisplay value={difference} isChangeValue className="font-bold text-xs" />
                </div>
            </div>
        );
    };

    // Prepare query parameters for export
    const getExportQueryParams = () => {
        // Get date range from filters - required for export
        const dateRangeFilter = expandedFilters.find((filter) => filter.filterKey === 'dateRange');
        if (!dateRangeFilter) {
            setNotification({
                text: 'Date range filter is required to export event history',
                level: 'warning',
            });
            return null;
        }

        let queryString = filtersToApiParams(expandedFilters, summarizePackout);

        // Strip out any sku or lot filters from the query string
        // Remove sku[]=XXX and lot[]=XXX from the query string if they exist
        queryString = queryString.replace(/[&?]sku\[\]=[^&]*/g, '');
        queryString = queryString.replace(/[&?]lot\[\]=[^&]*/g, '');
        // Fix the query string if it starts with '&' after removal
        if (queryString.startsWith('&')) {
            queryString = queryString.substring(1);
        }

        return queryString;
    };

    // Extract identity column (first one is the combined SKU/Item Name)
    const identityColumn = columns[0];
    // Rest are usually value columns
    const valueColumns = columns.slice(1);

    // Handle the compound skuWithName field
    const getSkuWithName = (row) => `${row.sku} (${row.itemName})`;

    // Handle the compound lotId field
    const getLotWithName = (row) => `${row.lotId} (${row.itemName})`;

    // Determine the item ID for the modal
    const getItemId = () => {
        // Return the appropriate ID based on the effective grouping mode
        if (effectiveGroupBy === 'lot' && row.lotId) {
            return row.lotId;
        }
        return row.sku || row.lotId || '';
    };

    // Generate the modal title with item ID and adjustment type
    const getModalTitle = () => {
        const baseTitle =
            adjustmentType === 'positive'
                ? 'Positive Adjustment Reasons'
                : adjustmentType === 'negative'
                  ? 'Negative Adjustment Reasons'
                  : 'Adjustment Reasons';

        // Determine which identifier to show based on the effective grouping mode
        if (effectiveGroupBy === 'lot' && row.lotId) {
            return `${baseTitle} - Lot: ${row.lotId}`;
        }
        if (row.sku) {
            return `${baseTitle} - SKU: ${row.sku}`;
        }
        if (row.lotId) {
            // Fallback in case groupBy is sku but only lotId is available
            return `${baseTitle} - Lot: ${row.lotId}`;
        }

        return baseTitle;
    };

    // Render the expanded card based on the row type
    const renderExpandedCard = () => {
        console.log(row.companySource === 'Multiple');
        // For SKU rows, pass the sku ID
        if (row.sku && !row.lotId) {
            return (
                <EventsExpandedContainer
                    type={groupByType}
                    companySourceId={row?.companySourceId}
                    skuOrLotId={row.sku}
                    expandedFilters={expandedFilters}
                    setNotification={setNotification}
                    filtersToApiParams={filtersToApiParams}
                    facilityId={facilityId}
                    summarizePackout={summarizePackout}
                    displaySource={row.companySource === 'Multiple'}
                />
            );
        }
        // For lot rows, pass the lot ID
        if (row.lotId) {
            return (
                <EventsExpandedContainer
                    type={groupByType}
                    skuOrLotId={row.lotId}
                    displaySource={row.companySource === 'Multiple'}
                    expandedFilters={expandedFilters}
                    setNotification={setNotification}
                    filtersToApiParams={filtersToApiParams}
                    facilityId={facilityId}
                    summarizePackout={summarizePackout}
                />
            );
        }
        return null;
    };

    return (
        <>
            {/* Main row */}
            <tr
                className={`row-hover transition-all duration-200 shadow hover:shadow-md ${
                    open ? 'rounded-t' : 'rounded'
                }`}
            >
                {/* Expand/collapse button cell */}
                <td className="p-2 text-center align-middle w-[5%]">
                    <button
                        aria-label="expand row"
                        className="p-1 rounded-full export-button-hover"
                        onClick={handleToggleExpand}
                    >
                        {open ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                        ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                        )}
                    </button>
                </td>

                {/* Combined SKU/Item Name cell */}
                <td className="p-2 font-bold overflow-hidden text-ellipsis text-center w-[20%]">
                    {identityColumn.id === 'skuWithName'
                        ? getSkuWithName(row)
                        : identityColumn.id === 'lotId'
                          ? getLotWithName(row)
                          : row[identityColumn.id] || ''}
                </td>

                {/* Value cells */}
                {valueColumns.map((column) => (
                    <td key={column.id} className="p-2 text-center">
                        {column.id === 'negativeAdjustments' ? (
                            <div
                                onClick={(event) => handleOpenAdjustmentModal('negative', event)}
                                className="inline-block cursor-pointer"
                            >
                                <NumberDisplay
                                    value={row[column.id] || 0}
                                    isChangeValue={isChangeValueColumn(column.id)}
                                    className="inline-block px-1 font-bold text-xs cursor-pointer"
                                />
                            </div>
                        ) : column.id === 'adjustedUpUnits' ? (
                            <div
                                onClick={(event) => handleOpenAdjustmentModal('positive', event)}
                                className="inline-block cursor-pointer"
                            >
                                <NumberDisplay
                                    value={row[column.id] || 0}
                                    isChangeValue={isChangeValueColumn(column.id)}
                                    className="inline-block px-1 font-bold text-xs"
                                />
                            </div>
                        ) : column.id === 'difference' ? (
                            <ValueTooltip
                                tooltipContent={createDifferenceTooltip(row)}
                                options={{ tooltipStyles: { minWidth: '320px' } }}
                            >
                                <NumberDisplay
                                    value={row[column.id] || 0}
                                    isChangeValue={isChangeValueColumn(column.id)}
                                    className="inline-block px-1 font-bold text-xs cursor-help"
                                />
                            </ValueTooltip>
                        ) : (
                            <NumberDisplay
                                value={row[column.id] || 0}
                                isChangeValue={isChangeValueColumn(column.id)}
                                className="inline-block px-1 font-bold text-xs"
                            />
                        )}
                    </td>
                ))}

                {/* Export button cell */}
                <td className="p-2 text-center align-middle w-[5%]">
                    {row.sku || row.lotId ? (
                        <ExportEventsButton
                            companySourceId={row?.companySourceId}
                            type={groupBy}
                            skuOrLotId={row.lotId || row.sku}
                            facilityId={facilityId}
                            queryParams={getExportQueryParams()}
                            setNotification={setNotification}
                        />
                    ) : null}
                </td>
            </tr>
            {open && (
                <tr className="expansion-row">
                    <td colSpan={columns.length + 2} className="p-0 border-0">
                        <div className="expanded-content-container">{renderExpandedCard()}</div>
                    </td>
                </tr>
            )}

            {/* Adjustment Reason Modal */}
            <AdjustmentReasonModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={getModalTitle()}
                fetchAdjustmentReasons={fetchAdjustmentReasons}
                companySourceId={row?.companySourceId}
                itemId={getItemId()}
                groupBy={effectiveGroupBy}
                adjustmentType={adjustmentType}
                packedOutUnits={row.packedOutUnits || 0}
            />
        </>
    );
}

export { CollapsibleRow };
